import React, { useRef } from "react";
import MainLayout from "../layout/MainLayout";
import { motion } from "framer-motion";
import { ArrowRight, PhoneCall } from "lucide-react";
import SelfDriveImg from "../assets/gallery/image-3.jpeg";
import DriverImg from "../assets/gallery/image-12.jpeg";
import { Link } from "react-router-dom";

const fadeInUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { delay },
});

export const WithOrWithoutDriver = () => {
  const comparisonRef = useRef(null);

  const scrollToComparison = () => {
    comparisonRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <MainLayout>
      {/* Hero Section */}
      <section className="relative h-[70vh] flex flex-col md:flex-row overflow-hidden rounded-3xl mx-4 sm:mx-10 my-6 shadow-2xl">
        <motion.div
          className="relative w-full md:w-1/2 h-full bg-cover bg-center"
          style={{ backgroundImage: `url(${SelfDriveImg})` }}
          {...fadeInUp(0.1)}
        >
          <div className="absolute inset-0 bg-black/50 flex flex-col justify-center text-center text-white p-6">
            <h1 className="text-3xl sm:text-5xl font-bold mb-2 just-me-again-down-here-regular">
              With or Without a Driver
            </h1>
            <p className="text-lg text-white/90 max-w-md mx-auto">
              Drive yourself or ride with a professional – the choice is yours.
            </p>
          </div>
        </motion.div>

        <motion.div
          className="w-full md:w-1/2 flex flex-col justify-center bg-gradient-to-br from-teal-700 to-teal-500 text-white p-10"
          {...fadeInUp(0.3)}
        >
          <p className="text-xl mb-4 font-light">
            Muhaad gives you the flexibility to choose between{" "}
            <span className="font-semibold">self-drive</span> and{" "}
            <span className="font-semibold">chauffeur-driven</span> rentals,
            depending on your comfort and travel needs.
          </p>
          <div>
            <button
              onClick={scrollToComparison}
              className="inline-flex items-center gap-2 bg-white text-teal-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
            >
              Compare Options <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      </section>

      {/* Comparison Section */}
      <section
        ref={comparisonRef}
        className="max-w-7xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-10"
      >
        {/* Self Drive */}
        <motion.div
          className="p-8 rounded-2xl bg-white dark:bg-white/10 shadow-xl border border-gray-200 dark:border-white/10"
          {...fadeInUp(0.1)}
        >
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-2xl font-bold just-me-again-down-here-regular">
              Pros of Self-Drive
            </h2>
          </div>

          <ul className="space-y-2 text-gray-700 dark:text-gray-300 text-lg">
            <li>• Full privacy and independence – explore at your own pace.</li>
            <li>• Ideal for short city errands or experienced local drivers.</li>
            <li>• Great for spontaneous travel plans.</li>
          </ul>

          <motion.div
            className="mt-8 overflow-hidden rounded-xl"
            whileHover={{ scale: 1.03 }}
          >
            <img
              src={SelfDriveImg}
              alt="Self Drive"
              className="rounded-xl object-cover w-full h-64 shadow-lg"
            />
          </motion.div>
        </motion.div>

        {/* With Driver */}
        <motion.div
          className="p-8 rounded-2xl bg-white dark:bg-white/10 shadow-xl border border-gray-200 dark:border-white/10"
          {...fadeInUp(0.2)}
        >
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-2xl font-bold just-me-again-down-here-regular">
              Pros of Hiring a Driver
            </h2>
          </div>

          <ul className="space-y-2 text-gray-700 dark:text-gray-300 text-lg">
            <li>• Stress-free travel – no parking hassles or traffic worries.</li>
            <li>• Time-saving – our drivers know the fastest routes.</li>
            <li>• Trip planning and local recommendations.</li>
            <li>• Professional, fluent, and courteous service.</li>
            <li>• Focus on comfort and security – we’ll handle the rest.</li>
            <li>• Safari & NGO-experienced for all travel needs.</li>
          </ul>

          <motion.div
            className="mt-8 overflow-hidden rounded-xl"
            whileHover={{ scale: 1.03 }}
          >
            <img
              src={DriverImg}
              alt="With Driver"
              className="rounded-xl object-cover w-full h-64 shadow-lg"
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Need Help Choosing Section */}
      <section className="text-center py-16 px-6 bg-gradient-to-br from-teal-700 to-teal-900 text-white rounded-3xl mx-4 sm:mx-10 my-10 shadow-2xl">
        <motion.h3
          {...fadeInUp(0.1)}
          className="text-3xl md:text-5xl font-bold mb-4 just-me-again-down-here-regular"
        >
          ⚖️ Need Help Choosing?
        </motion.h3>
        <motion.p
          {...fadeInUp(0.2)}
          className="max-w-3xl mx-auto text-lg text-white/90 mb-8"
        >
          Let us know your travel plans — we’ll recommend the best option based
          on your route, duration, and type of trip.
        </motion.p>
        <motion.div
          {...fadeInUp(0.3)}
          className="flex flex-wrap justify-center gap-4"
        >
          <button
            onClick={scrollToComparison}
            className="bg-white text-teal-800 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition flex items-center gap-2"
          >
            Compare Options <ArrowRight className="w-5 h-5" />
          </button>
          <a
            href="tel:+256784263189"
            className="bg-teal-600 hover:bg-teal-500 px-6 py-3 rounded-lg font-semibold transition flex items-center gap-2"
          >
            <PhoneCall className="w-5 h-5" /> Talk to a Travel Consultant
          </a>
        </motion.div>
      </section>
    </MainLayout>
  );
};
